import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { SessionGuardService } from './session-guard.service';
import { reasoningHeader } from './reasoning-header.util';
import { ClientService } from './client.service';

/** Mirrors AtlasSelection in olympus-functions. EITHER picks OR creates — never both. */
export interface AtlasSelectionResult {
  picks?: { id: string; why: string }[];
  creates?: { name: string; type: string; scope: 'local' | 'global'; why: string }[];
  warnings?: string[];
}

/**
 * S3 (client) — one call to `atlasSelect`.
 *
 * A PROMISE, not a stream, unlike ForgeService. Forge streams because a generation has phases a builder
 * watches (planning, validating, fixing) and each is worth showing. A selection has one answer and the
 * caller cannot act on a partial one — it either has the ids or it does not — so a stream would give the
 * orchestration a state machine to manage for no gain. The CF still speaks SSE (same wrapper as Forge);
 * this collapses it at the seam.
 */
@Injectable({ providedIn: 'root' })
export class AtlasSelectService {
  private authService = inject(AuthService);
  private sessionGuard = inject(SessionGuardService);
  private client = inject(ClientService);

  async select(
    body: { prompt: string; pickList: string; functionName?: string; round?: number },
    signal?: AbortSignal,
  ): Promise<AtlasSelectionResult> {
    const token = await this.authService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(`${environment.functionsBaseUrl}/atlasSelect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Session-Id': this.sessionGuard.getSessionId(),
        ...reasoningHeader(),
        // See forge.service.ts — AtlasRequest carries no clientId either.
        'X-Zavo-Client-Id': this.client.selectedClientId,
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) throw new Error(`atlasSelect ${res.status}`);

    // Read the SSE to completion and keep the LAST `selection` / first `error`. Events other than those
    // are progress, which nothing here needs.
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '', event = '', result: AtlasSelectionResult | null = null, error: string | null = null;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) { event = line.slice(7).trim(); continue; }
        if (!line.startsWith('data: ')) continue;
        let data: any;
        try { data = JSON.parse(line.slice(6)); } catch { continue; }
        if (event === 'selection') result = data;
        else if (event === 'error' && !error) error = data?.message || 'Atlas failed';
      }
    }
    if (error) throw new Error(error);
    if (!result) throw new Error('Atlas returned no selection');
    return result;
  }
}
